import type { ReactNode } from 'react'
import { Module } from './Module'

interface StatModuleProps {
  title: string
  caption?: string
  drawerKey: string
  span?: number
  value: string
  tone?: 'gain' | 'loss' | 'warn' | 'neutral'
  compare?: ReactNode
  footer?: ReactNode
  isLoading?: boolean
  isError?: boolean
  errorText?: string
  /** A fact, not a failure: nothing to show (e.g. a dormant book). Shown after the error rung. */
  isEmpty?: boolean
  emptyText?: string
}

/** Row-1 stat tile: a Module whose face is one big value plus compare/footer. */
export function StatModule({
  title,
  caption,
  drawerKey,
  span = 2,
  value,
  tone = 'neutral',
  compare,
  footer,
  isLoading,
  isError,
  errorText,
  isEmpty,
  emptyText,
}: StatModuleProps) {
  return (
    <Module
      title={title}
      caption={caption}
      drawerKey={drawerKey}
      span={span}
      isError={isError && !isLoading}
      errorText={errorText}
      // Loading keeps the tile's own `—` (it is not handed to Module), so an
      // empty rung only shows once the tile has settled; Module's ladder then
      // puts an error before it.
      isEmpty={isEmpty && !isLoading}
      emptyText={emptyText}
      className="module--stat"
    >
      <div
        className={`stat-value num stat-value--${tone}`}
        aria-busy={isLoading || undefined}
      >
        {isLoading ? '—' : value}
      </div>
      <div className="stat-compare caption">{compare}</div>
      {/* Footer omitted while loading; the tile keeps its height (spec §6.2). */}
      {!isLoading && <div className="stat-footer">{footer}</div>}
    </Module>
  )
}
